import {createForm, formSubscriptionItems} from 'final-form';
import {isBool} from '@gl/lit/utils/index.js';
import {ref} from 'lit/directives/ref.js';
import type {DirectiveResult} from 'lit/directive.js';
import type {
  Config,
  FormApi,
  FormState,
  FormSubscription,
  Unsubscribe,
  FieldConfig,
  FieldSubscriber,
  FieldSubscription,
  ValidationErrors,
  SubmissionErrors,
} from 'final-form';
import type {
  AllPropertiesUndefined,
  IsSameType,
} from '@gl/lit/types/general.js';
import type {MsgVariant} from '@gl/lit/types/architecture.js';
import type {IImmutableComponent} from '@gl/lit/components/mixins/immutable_component/index.js';
import type {Nullable} from '@gl/lit/types/general.js';
import {debounce} from '@gl/utils/debounce.js';
import type {JsonRecord} from 'fp-ts/lib/Json.js';

/** An atomic unit of form data; used to compose actual form values. */
type ImmutableFormValueAtom =
  | string
  | number
  | boolean
  | JsonRecord
  | JsonRecord[];
/**
 * A type enumerating all valid form values to use when defining form data.
 */
export type ImmutableFormValue =
  | Nullable<ImmutableFormValueAtom>
  | ImmutableFormValueAtom[];
/**
 * Given the tag of a registered form element, this utility type returns the
 * native ImmutableFormValue the component uses.
 */
export type ElementFieldType<T extends keyof ImmutableFormTypeMap> =
  ImmutableFormTypeMap[T] extends ImmutableFormValue
    ? ImmutableFormTypeMap[T]
    : never;
/**
 * Utility type for generating an immutable form field.
 */
export type Field<
  T extends ImmutableFormValue,
  U extends keyof ImmutableFormTypeMap,
> = {
  value: T;
  element: U;
};
/**
 * This is primary type to use for representing the fields for a given form.
 */
export type ImmutableFormFields = Record<
  PropertyKey,
  {value: ImmutableFormValue; element: keyof ImmutableFormTypeMap}
>;
/**
 * Utility type that creates a map of ImmutableFormValues for the
 * elements associated with given fields.
 */
export type ElementValues<T extends ImmutableFormFields> = {
  [K in keyof T]: ElementFieldType<T[K]['element']>;
};
/**
 * Utility type that creates a map of the ImmutableFormValues for
 * each form field.
 */
export type FieldValues<T extends ImmutableFormFields> = {
  [K in keyof Required<T>]-?: undefined extends T[K]
    ? Required<T>[K]['value'] | undefined
    : T[K]['value'];
};
/**
 * An ImmutableFormTransformation defines a constract for how a given field value
 * can be converted into or generated from the value of its associated element.
 */
type ImmutableFormTransformation<
  K extends PropertyKey,
  T extends ImmutableFormFields,
> = {
  to: (
    elementValue: ElementValues<T>[K],
    element: HTMLElement
  ) => FieldValues<T>[K];
  from: (value: FieldValues<T>[K], element: HTMLElement) => ElementValues<T>[K];
};
/**
 *  A type representing a map of form field keys to transformation contracts.
 */
type ImmutableFormTransformations<T extends ImmutableFormFields> = {
  [K in keyof T as IsSameType<
    FieldValues<T>[K],
    ElementValues<T>[K]
  > extends true
    ? never
    : K]: ImmutableFormTransformation<K, T>;
};

export type ImmutableFormFieldErrors<Fields extends ImmutableFormFields> =
  Partial<
    Record<keyof FieldValues<Fields>, {error: FieldError; touched: boolean}>
  > &
    ValidationErrors;

type StateWithTypedErrors<Fields extends ImmutableFormFields> = Omit<
  FormState<FieldValues<Fields>, Partial<FieldValues<Fields>>>,
  'errors'
> & {
  errors?: Partial<ImmutableFormFieldErrors<Fields>>;
};

type ChangeSubscriptionKeyPart<FieldKey extends keyof ImmutableFormFields> =
  FieldKey extends string | number ? `${FieldKey}` : never;

type Part<FieldKey extends keyof ImmutableFormFields> =
  ChangeSubscriptionKeyPart<FieldKey>;

/* eslint-disable max-len */
type ChangeSubscriptionKey<F extends keyof ImmutableFormFields> =
  | Part<F>
  | `${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>}`
  | `${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>},${Part<F>}`;
/* eslint-enable max-len */

export type ChangeSubscriptionHandler<Fields extends ImmutableFormFields> = (
  state: ImmutableFormState<Fields>
) => void | Promise<void>;

export type SubscriptionHandler<Fields extends ImmutableFormFields> = (
  state: ImmutableFormState<Fields>,
  unsubscribe: () => void
) => void | Promise<void>;

type BaseConfig<
  Fields extends ImmutableFormFields,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Host extends IImmutableComponent<any, any> = IImmutableComponent<any, any>,
> = {
  mutators?: Config<FieldValues<Fields>>['mutators'];
  debug?: Config<FieldValues<Fields>>['debug'];
  destroyOnUnregister?: Config<FieldValues<Fields>>['destroyOnUnregister'];
  // TODO: Review Model type
  onSubmit: (
    values: FieldValues<Fields>,
    form: FormApi<FieldValues<Fields>, Partial<FieldValues<Fields>>>,
    model?: Host['model'],
    callback?: (errors?: SubmissionErrors) => void
  ) => SubmissionErrors | Promise<SubmissionErrors> | void;
  validateOnBlur?: Config<FieldValues<Fields>>['validateOnBlur'];
  keepDirtyOnReinitialize?: Config<
    FieldValues<Fields>
  >['keepDirtyOnReinitialize'];
  initialValues?: Config<FieldValues<Fields>>['initialValues'];
  // TODO: Review Model type
  validate?: (
    values: FieldValues<Fields>,
    errors: ImmutableFormFieldErrors<Fields>,
    model?: Host['model']
  ) => ImmutableFormFieldErrors<Fields>;
};

/**
 * ImmutableForm config type that includes the transformation map.
 * (Used to compose the real config object, making transformations conditionally
 * optional)
 */
type ConfigWithTransformations<
  Fields extends ImmutableFormFields,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Host extends IImmutableComponent<any, any> = IImmutableComponent<any, any>,
> = Omit<BaseConfig<Fields, Host>, 'transformations'> & {
  transformations: ImmutableFormTransformations<Fields>;
};
/**
 * ImmutableForm config type that excludes the transformation map.
 * (Used to compose the real config object, making transformations conditionally
 * optional)
 */
type ConfigWithoutTransformations<
  Fields extends ImmutableFormFields,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  _Host extends IImmutableComponent<any, any> = IImmutableComponent<any, any>,
> = Omit<BaseConfig<Fields>, 'transformations'> & {
  transformations?: undefined;
};
/**
 * Configuration object to use when first creating ImmutableForms.
 */
type ImmutableFormConfig<
  Fields extends ImmutableFormFields,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Host extends IImmutableComponent<any, any> = IImmutableComponent<any, any>,
> =
  AllPropertiesUndefined<ImmutableFormTransformations<Fields>> extends true
    ? ConfigWithoutTransformations<Fields, Host>
    : ConfigWithTransformations<Fields, Host>;
/**
 * A complete contract for how any given element can be used by the
 * ImmutableForm. Meant to be used in conjunction with the form registry.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ImmutableFormElementDef<T extends HTMLElement, Value = any> = {
  predicate?: (el: Element) => boolean;
  tagName: string;
  register?: (el: T) => void;
  reset: (el: T) => void;
  blur: (el: T, trigger: () => void) => void;
  change: (el: T, trigger: (value: ImmutableFormValue) => void) => void;
  focus: (el: T, trigger: () => void) => void;
  setName: (el: T, name: PropertyKey) => void;
  getValue: (el: T) => Value;
  setValue: (el: T, value: unknown) => void;
};

/** Shorthand for accessing the final-form API. */
export type ImmutableFormApi<Values> = FormApi<Values, Partial<Values>>;
/** Shorthand for accessing the final-form state, given ImmutableFormFields. */
export type ImmutableFormState<Fields extends ImmutableFormFields> =
  StateWithTypedErrors<Fields>;

/**
 * Shorthand for accessing the final-form subscription handler,
 * given ImmutableFormFields, etc.
 */
export type ImmutableFormStateSubscription<
  Fields extends ImmutableFormFields,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Host extends IImmutableComponent<any, any> = IImmutableComponent<any, any>,
> =
  | ((
      state: ImmutableFormState<Fields>
    ) => MsgVariant<string, ImmutableFormState<Fields>>)
  | ((state: ImmutableFormState<Fields>, host: Host) => void);

const initialSubscriptionItems = formSubscriptionItems.reduce<
  FormSubscription & {[key: string]: unknown}
>((acc, item) => ((acc[item] = true), acc), {});

/**
 * Static form element registry that allows
 * individual components to determine, in advance,
 * how they want to interface
 * with the final-form's subscription events.
 */
export class ImmutableFormRegistry {
  private static registry: Map<
    string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ImmutableFormElementDef<any>
  > = new Map();

  static register<T extends HTMLElement, Value>(
    tagName: string,
    definition: Omit<ImmutableFormElementDef<T, Value>, 'tagName'>
  ) {
    ImmutableFormRegistry.registry.set(tagName.toUpperCase(), {
      tagName,
      ...definition,
    });
  }

  static has(tagName: string) {
    return ImmutableFormRegistry.registry.has(tagName);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  static get(tagName: string): Nullable<ImmutableFormElementDef<any>> {
    return ImmutableFormRegistry.registry.get(tagName) ?? null;
  }
}

function decorateState<Fields extends ImmutableFormFields>(
  state: FormState<FieldValues<Fields>, Partial<FieldValues<Fields>>>
): ImmutableFormState<Fields> {
  const errors = Object.entries(state.errors ?? {}).reduce(
    (acc, [key, err]) => {
      if (err) {
        Object.assign(acc, {
          [key]: {
            error: err.error as FieldError,
            touched: state.touched?.[key],
          },
        });
      }
      return acc;
    },
    Object.create(null)
  );

  return {
    ...state,
    errors: errors as ImmutableFormFieldErrors<Fields>,
  };
}

export class ImmutableFormElementRegistry {
  private static registry: Set<Element> = new Set();

  static register<T extends HTMLElement>(element: T) {
    console.log(`saving ${element.dataset?.testId}`);
    ImmutableFormElementRegistry.registry.add(element);
  }

  static delete<T extends HTMLElement>(element: T) {
    console.log(`deleting ${element.dataset?.testId}`);
    ImmutableFormElementRegistry.registry.delete(element);
  }

  static has(element: HTMLElement) {
    const hasIt = ImmutableFormElementRegistry.registry.has(element);
    console.log(
      hasIt
        ? `${element.dataset?.testId} already exists`
        : `create ${element.dataset?.testId}`
    );
    return hasIt;
  }
}

/**
 * An ImmutableForm is a wrapper around the "Final Form" library that
 * allows for immutable components to tap into the final-form api.
 */
export class ImmutableForm<
  FormFields extends ImmutableFormFields,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Host extends IImmutableComponent<any, any> = IImmutableComponent<any, any>,
> {
  unsubscribe?: Unsubscribe;

  private form: ImmutableFormApi<FieldValues<FormFields>>;
  private primarySubscription: FormSubscription = initialSubscriptionItems;
  private stateHandler?: ImmutableFormStateSubscription<FormFields, Host>;
  private host: Nullable<Host> = null;
  private transformations?: ImmutableFormTransformations<FormFields>;
  private subscriptions: Map<
    FormSubscription,
    {
      handler: SubscriptionHandler<FormFields>;
      unsubscribe: Unsubscribe;
    }
  > = new Map();
  private changeSubscriptions: Map<
    ChangeSubscriptionKey<keyof FormFields>,
    {
      fields: Array<keyof FormFields>;
      handlers: Set<ChangeSubscriptionHandler<FormFields>>;
    }
  > = new Map();
  private directives = new Map<
    keyof FormFields,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    {directive: DirectiveResult<any>; unsubscribe: Unsubscribe}
  >();
  private elements = new Map<
    keyof FormFields,
    {
      element: HTMLElement;
      definition: ImmutableFormElementDef<HTMLElement>;
    }
  >();

  protected constructor(config: ImmutableFormConfig<FormFields, Host>) {
    const {transformations, validate, onSubmit, ...rest} = config;

    this.form = createForm({
      ...rest,
      onSubmit: (values, form, callback) => {
        const result = onSubmit(values, form, this.host?.model, callback);
        return result;
      },
      validate: values => {
        const result = validate?.(values, {}, this.host?.model) ?? {};
        return result;
      },
    });

    this.transformations = transformations;
    this.listenValueChanges();
  }

  /**
   * Creates an ImmutableForm given a valid config object.
   */
  static create<
    FormFields extends ImmutableFormFields,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Host extends IImmutableComponent<any, any> = IImmutableComponent<any, any>,
  >(config: ImmutableFormConfig<FormFields, Host>) {
    return new ImmutableForm<FormFields, Host>(config);
  }

  private attachStateHandler() {
    if (!this.host) {
      console.error('You forgot to attach your form during init');
      return;
    }

    this.unsubscribe = this.form.subscribe(() => {
      const result = this.stateHandler?.(this.getState(), this.host!);
      if (result) this.host!.handleUpdate(result);
    }, this.primarySubscription);
  }

  private createSubscriptionKey(keys: Array<keyof FormFields>) {
    return keys.join(',') as ChangeSubscriptionKey<keyof FormFields>;
  }

  private listenValueChanges() {
    let cache = structuredClone(this.getState().values);

    const runSubscriptions = () => {
      const current = this.getState().values;
      this.changeSubscriptions.forEach(({fields, handlers}) => {
        const hasChanged = fields.some(
          field => !Object.is(cache[field], current[field])
        );
        if (hasChanged) {
          handlers.forEach(handler => handler(this.getState()));
        }
      });
      cache = structuredClone(current);
    };
    const debounced = debounce(runSubscriptions, 100);

    this.unsubscribe = this.form.subscribe(debounced, {values: true});

    if (this.host && 'disconnectSignal' in this.host) {
      (this.host.disconnectSignal as AbortSignal).addEventListener(
        'abort',
        () => {
          this.unsubscribe?.();
        }
      );
    }
  }

  getState() {
    return decorateState(this.form.getState());
  }

  isRegistered(name: keyof FormFields): boolean {
    return this.elements.has(name);
  }

  /**
   * Registers a field with final-form (see their docs for reference)
   * Only use this if you know what you're doing.
   *
   * @param name - The name of field; has to be a valid field key.
   * @param subscriber - A subscriber object (see final-form docs)
   * @param config - (optional) A raw final-form field config object.
   */
  registerField(
    name: keyof FieldValues<FormFields>,
    subscriber: FieldSubscriber<
      FieldValues<FormFields>[keyof FieldValues<FormFields>]
    >,
    subscription: FieldSubscription,
    element: HTMLElement,
    definition: ImmutableFormElementDef<HTMLElement>,
    config?: FieldConfig<FieldValues<FormFields>[keyof FieldValues<FormFields>]>
  ) {
    this.elements.set(name, {element, definition});
    const unregisterField = this.form.registerField(
      name,
      subscriber,
      subscription,
      config
    );

    const unsubscribe = () => {
      unregisterField();
      this.elements.delete(name);
    };

    return unsubscribe;
  }

  /**
   * Given a valid from field key, return the corresponding transformation.
   * @param name - The name of a valid form field key.
   */
  getTransformation(
    name: keyof FieldValues<FormFields>
  ): Nullable<ImmutableFormTransformation<typeof name, FormFields>> {
    if (!this.transformations || !(name in this.transformations)) return null;
    // FIXME(joshua): Make this cast a little nicer.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (this.transformations as any)[name] ?? null;
  }

  /**
   * Initializes the immutable form by attaching the form to the host of an
   * immutable component + the provided state handler, if it exists.
   *
   * @param host - The host of the immutable component.
   * @param params.stateHandler - An immutable Msg to call whenever form state changes.
   */
  init(
    host: Host,
    {
      stateHandler,
      initialValues,
    }: {
      stateHandler?: ImmutableFormStateSubscription<FormFields, Host>;
      initialValues?: Partial<FieldValues<FormFields>>;
    } = {}
  ): ImmutableFormState<FormFields> {
    this.host = host;
    this.stateHandler = stateHandler;
    this.attachStateHandler();

    if (initialValues) {
      const values = Object.entries(initialValues);
      values.forEach(([key, value]) => {
        this.form.change(key, value);
      });
    }

    return this.getState();
  }

  onChange(
    fields: Array<keyof FormFields>,
    handler: ChangeSubscriptionHandler<FormFields>,
    options?: {
      once?: boolean;
      runOnRegister?: boolean;
    }
  ) {
    const key = this.createSubscriptionKey(fields);
    const set = this.changeSubscriptions.get(key)?.handlers ?? new Set();
    const abortController = new AbortController();
    const fn = options?.once
      ? (state: ImmutableFormState<FormFields>) => {
          handler(state);
          abortController.abort();
        }
      : handler;
    set.add(fn);
    this.changeSubscriptions.set(key, {
      fields,
      handlers: set,
    });

    if (options?.runOnRegister) handler(this.getState());

    const unsubscribe = () => {
      const set = this.changeSubscriptions.get(key)?.handlers;
      set?.delete(handler);
      if (set?.size === 0) this.changeSubscriptions.delete(key);
    };

    if (options?.once) {
      abortController.signal.addEventListener('abort', unsubscribe);
    }

    return unsubscribe;
  }

  /**
   * Attach an event handler to be fired every time form state changes.
   * @param handler - The callback to be fired whenever state changes.
   */
  subscribe(
    handler: SubscriptionHandler<FormFields>,
    formSubscription?: FormSubscription
  ): Unsubscribe {
    const subscription: FormSubscription =
      formSubscription ?? initialSubscriptionItems;
    const remove = () => this.subscriptions.get(subscription)?.unsubscribe;
    const unsubscribe = this.form.subscribe(() => {
      const result = handler(this.getState(), remove);

      if (result instanceof Promise) {
        (async () => {
          try {
            await result;
          } catch (err) {
            console.error(err);
          }
        })();
      }
    }, subscription);

    this.subscriptions.set(subscription, {handler, unsubscribe});
    return unsubscribe;
  }

  set(key: keyof FormFields, value: FieldValues<FormFields>[typeof key]) {
    if (!this.elements.has(key)) return;

    const {element, definition} = this.elements.get(key)!;
    const transformation = this.getTransformation(key);

    this.form.change(key, value);

    definition.setValue(
      element,
      transformation ? transformation.from(value, element) : value
    );
  }

  setMultiple(updates: Partial<FieldValues<FormFields>>) {
    Object.entries(updates).forEach(([key, value]) => {
      this.set(key as keyof FormFields, value);
    });
  }

  submit() {
    this.form.submit();
  }

  /**
   * A Lit directive for registering a valid form element with form and
   * attaching it to the corresponding field within final-form.
   *
   * @param name - The name of the field; must be a valid field key.
   * @param fieldConfig - (optional) A field config object (see final-form docs)
   */
  register<K extends keyof FormFields>(
    name: K,
    fieldConfig?: FieldConfig<FieldValues<FormFields>[K]>
  ) {
    if (this.directives.has(name)) {
      return this.directives.get(name)!.directive;
    }

    const directive = ref(element => {
      if (!(element instanceof HTMLElement)) return;

      const el = element;
      const definition = ImmutableFormRegistry.get(el.tagName.toUpperCase());

      if (!definition) return;

      const unregisterField = this.registerField(
        name,
        fieldState => {
          const {blur, change, focus, value} = fieldState;
          const transformation = this.getTransformation(name);

          definition.setName(el, name);

          if (
            // @ts-expect-error hack
            !el.formListenersAttached
          ) {
            definition.blur(el, blur);
            definition.change(el, value => {
              change(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                transformation ? transformation.to(value as any, el) : value
              );
            });
            definition.focus(el, focus);
            // @ts-expect-error hack
            el.formListenersAttached = true;
          }

          definition.setValue(
            el,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            transformation ? transformation.from(value as any, el) : value
          );
        },
        {value: true, dirty: true, touched: true, valid: true},
        el,
        definition,
        fieldConfig
      );

      const unsubscribe = () => {
        unregisterField();
        this.directives.delete(name);
      };

      if ('disconnectSignal' in el) {
        (el.disconnectSignal as AbortSignal).addEventListener('abort', () => {
          unsubscribe();
        });
      }

      this.directives.set(name, {directive, unsubscribe});
    });

    return directive;
  }

  unregisterField(name: keyof FormFields) {
    if (this.directives.has(name)) {
      this.directives.get(name)!.unsubscribe();
    }
  }

  /**
   * A utility function that takes a boolean or a predicate function and
   * calls one of two functions depending on whether the boolean/predicate
   * is true.
   *
   * @param predicate - The boolean or predicate fn to be called.
   * @param whenTrue - The function to be fired when the predicate is true.
   * @param whenFalse - The function to be fired when the predicate is false.
   */
  when<T>(
    predicate:
      | boolean
      | ((values: ImmutableFormState<FormFields>['values']) => boolean),
    whenTrue: () => T,
    whenFalse?: () => ReturnType<typeof whenTrue>
  ) {
    return isBool(predicate)
      ? predicate
        ? whenTrue()
        : whenFalse?.()
      : predicate(this.form.getState().values)
        ? whenTrue()
        : whenFalse?.();
  }
}

export type FieldErrorKey = 'required' | 'min-length' | 'max-length' | 'custom';

export class FieldError extends Error {
  name = 'ImmutableFieldError';
  key: FieldErrorKey;

  constructor(key: FieldErrorKey, message: string) {
    super(message);
    this.key = key;
  }

  static required(message = 'Required'): FieldError {
    return new FieldError('required', message);
  }

  static minLength(message = 'Value is too short'): FieldError {
    return new FieldError('min-length', message);
  }

  static maxLength(message = 'Value is too long'): FieldError {
    return new FieldError('max-length', message);
  }

  static custom(message: string): FieldError {
    return new FieldError('custom', message);
  }
}
